import type { ReactNode } from 'react'
import ShieldOutlinedIcon from '@mui/icons-material/ShieldOutlined'
import EventAvailableRoundedIcon from '@mui/icons-material/EventAvailableRounded'
import FavoriteBorderRoundedIcon from '@mui/icons-material/FavoriteBorderRounded'
import ExploreOutlinedIcon from '@mui/icons-material/ExploreOutlined'
import WarningAmberRoundedIcon from '@mui/icons-material/WarningAmberRounded'
import MailOutlineRoundedIcon from '@mui/icons-material/MailOutlineRounded'
import PhoneOutlinedIcon from '@mui/icons-material/PhoneOutlined'
import FolderOutlinedIcon from '@mui/icons-material/FolderOutlined'
import EventBusyRoundedIcon from '@mui/icons-material/EventBusyRounded'
import HowToRegRoundedIcon from '@mui/icons-material/HowToRegRounded'
import type { TimelineEvent, TimelineLane } from './MemberTimelineService'
import { memberSurfacePath } from '~/entities/person'
import type { MemberIds } from '~/entities/person'

/**
 * The lane vocabulary, fixed by the endpoint's schema. Labels are the client's own and name
 * what a lane actually holds — the schema keys are broad enough ("clinical", "ops") that staff
 * guessed wrong about where events lived. Order here is the order the filter chips render in.
 */
export const LANES: Array<{ lane: TimelineLane; label: string; icon: ReactNode }> = [
  { lane: 'coverage', label: 'Coverage & enrollment', icon: <ShieldOutlinedIcon /> },
  { lane: 'visits', label: 'Visits', icon: <EventAvailableRoundedIcon /> },
  { lane: 'clinical', label: 'Care & programs', icon: <FavoriteBorderRoundedIcon /> },
  { lane: 'navigation', label: 'Navigation & claims', icon: <ExploreOutlinedIcon /> },
  { lane: 'escalations', label: 'Urgent & emergency', icon: <WarningAmberRoundedIcon /> },
  { lane: 'ops', label: 'Cases & contact', icon: <MailOutlineRoundedIcon /> },
]

export const ALL_LANES: TimelineLane[] = LANES.map(({ lane }) => lane)

const LANE_DISPLAY = new Map(LANES.map(entry => [entry.lane, entry]))

/**
 * Icons for the event kinds a lane's own icon would misrepresent. A lane icon is a reasonable
 * default while a lane has one kind of event, but the ops lane alone carries mail, cases and
 * calls — all of which would otherwise be envelopes.
 *
 * `type` is a free string in the schema so that new event kinds ship without a client change;
 * one missing from here falls back to its lane's icon rather than rendering nothing.
 */
const EVENT_TYPE_ICONS: Record<string, ReactNode> = {
  mail_requested: <MailOutlineRoundedIcon />,
  mail_acknowledged: <MailOutlineRoundedIcon />,
  mail_sent: <MailOutlineRoundedIcon />,
  mail_status_change: <MailOutlineRoundedIcon />,
  case_opened: <FolderOutlinedIcon />,
  case_closed: <FolderOutlinedIcon />,
  case_reopened: <FolderOutlinedIcon />,
  phone_call_inbound: <PhoneOutlinedIcon />,
  phone_call_outbound: <PhoneOutlinedIcon />,
  visit_missed: <EventBusyRoundedIcon />,
  visit_cancelled: <EventBusyRoundedIcon />,
  signed_up: <HowToRegRoundedIcon />,
  account_reactivated: <HowToRegRoundedIcon />,
}

export const eventIcon = (event: TimelineEvent): ReactNode =>
  EVENT_TYPE_ICONS[event.type] ?? LANE_DISPLAY.get(event.lane)?.icon

export const eventLinkPath = (event: TimelineEvent, member: MemberIds): string | null =>
  event.link == null
    ? null
    : memberSurfacePath(event.link.object_type, event.link.object_id, member)
